import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Eraser, Check } from '@phosphor-icons/react';

const SignaturePad = ({ onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasUserStrokes, setHasUserStrokes] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
  }, []);

  const getCoord = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    const { x, y } = getCoord(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasUserStrokes(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoord(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stop = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
    setHasUserStrokes(false);
  };

  const save = () => {
    if (!hasUserStrokes) {
      return;
    }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div className="space-y-3">
      <div className="text-sm text-slate-600">Signez avec le doigt (mobile/tablette) ou la souris dans le cadre ci-dessous.</div>
      <div className="border-2 border-dashed border-slate-300 rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          className="w-full h-48 touch-none cursor-crosshair block"
          style={{ width: '100%', height: '192px' }}
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={stop}
        />
      </div>
      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={clear} data-testid="signature-clear-btn">
          <Eraser size={16} className="mr-1" /> Effacer
        </Button>
        <div className="flex gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} data-testid="signature-cancel-btn">Annuler</Button>
          )}
          <Button
            type="button"
            onClick={save}
            disabled={!hasUserStrokes}
            data-testid="signature-save-btn"
            className="bg-blue-600 hover:bg-blue-500 text-white"
          >
            <Check size={16} className="mr-1" /> Valider la signature
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignaturePad;
